import * as React from 'react';

import Mentions from 'antd/lib/mentions';
import Tag from 'antd/lib/tag';
import { FieldProps, Form, Formik, FormikProps } from 'formik';
import * as Yup from 'yup';
import VerticalAlignBottomOutlined from '@ant-design/icons/VerticalAlignBottomOutlined';
import message from 'antd/lib/message';

import { Chat, ChatMessage, ChatMessageType } from '@common/react/objects/Chat';
import Button from '@common/react/components/Forms/Button';
import FormikField from '@common/react/components/Forms/FormikField/FormikField';
import { getUserName } from '@common/react/utils/utils';
import { BaseUser, BaseUserWithAvatar } from '@common/react/objects/BaseUser';
import { FileInterface, FileType } from '@common/typescript/objects/FileInterface';
import FormikRef from '@common/react/components/Core/ItemEditor/FormikRef';
import { RecordResult } from '@common/react/utils/record-audio';
import { uploadFile } from '@common/react/components/Forms/Files/File';
import {
	ChatComponents,
	ChatFormButtonsDictionary,
	ChatFormComponentsDictionary,
	ChatFormTag,
	ChatFormTagDictionary,
	useChatSettingsProviderContext
} from '@common/react/components/Chat/ChatSettingsProvider';
import useRequest from '@common/react/hooks/useRequest';
import { imageExtensions } from '@common/react/components/Chat/Chats';

export interface ButtonsProps {
	onUploadFile: (file: FileInterface<BaseUser>) => void;
}

interface ComponentProps {
	chat: Chat;
	editMessage: ChatMessage | null;
	setEdit: (message) => void;
	chatMessageRequest?: string;
	label?: string;
	additionalFiles?: (formikBag: FormikProps<NewMessage>, {state, setState}) => React.ReactNode;
	afterSubmit?: (message) => void;
	sendClassName?: string;
	onImageClick?: (e, file: FileInterface<BaseUser>) => void;
}

interface ContactsHash {
	[index: number]: string;
}

export interface ChatFormComponentState {
	isLoading: boolean;
	contacts: ContactsHash;

	recordAudioResult: RecordResult | undefined;
	recordVideoResult: RecordResult | undefined;
}

export interface NewMessage {
	id: number;
	text: string;
	chatId: number;

	bytes: number[];
	files: Array<FileInterface>;
	attachments: Array<number>;
	chatMessageType: ChatMessageType;
}

const getValidationSchema = (maxLength: number) => Yup.object().shape({
	text: Yup.string().max(maxLength, `max characters count: ${maxLength}`),
});

const transformContactsToHash = (contacts: BaseUserWithAvatar[]) => contacts.reduce((map, obj) => {
	map[obj.id] = getUserName(obj);
	return map;
}, {});

const ChatMessageForm: React.FC<ComponentProps> = (props) => {
	const {sendClassName = 'btn-primary', chat, onImageClick, editMessage, setEdit} = props;
	const form = React.useRef<FormikProps<NewMessage> | null>(null);
	const request = useRequest();

	const context = useChatSettingsProviderContext();

	if (!context?.state) throw 'need ChatSettingsContext';

	const {state: {formButtons, chatFormButtonsWrappers, requests, otherComponents, formSettings, formTags}} = context;
	const {maxAttachmentsCount, maxMessageLength, waveColor} = formSettings;

	const [state, _setState] = React.useState<ChatFormComponentState>({
		isLoading: false,
		contacts: props.chat ? transformContactsToHash(props.chat.contacts) : {},
		recordAudioResult: undefined,
		recordVideoResult: undefined
	});
	const setState = React.useCallback(newState => _setState(state => ({...state, ...newState})), []);

	React.useEffect(() => {
		setState({
			contacts: props.chat ? transformContactsToHash(props.chat.contacts) : {}
		});
	}, [props.chat]);

	const handleSubmitForm = (values: NewMessage, { resetForm }) => {
		const {text, files, attachments, bytes} = values;
		if (!(text || files?.length || attachments?.length || bytes?.length)) return;

		setState({
			isLoading: true
		});

		const item = values;

		item.chatId = props.chat.id;
		item.attachments = item.files.map(f => f.id);

		request(editMessage && requests.updateMessage ? requests.updateMessage : requests.chatMessage, item).then((result) => {
			setState({
				isLoading: false
			});
			resetForm();
			_setState(prev => ({
				...prev,
				recordAudioResult: undefined,
				recordVideoResult: undefined
			}));
			props.afterSubmit && props.afterSubmit(result);
		}).catch(() => {
			setState({
				isLoading: false
			});
		});
	};

	const onUploadFile = (file: FileInterface<BaseUser>, formikBag) => {
		const nextFiles = formikBag.values.files.concat(file);

		if (nextFiles.length > maxAttachmentsCount) {
			message.info(`Max Attachment files count: ${maxAttachmentsCount}`);
			return;
		}

		formikBag.values.files = nextFiles;
		formikBag.setFieldValue('files', nextFiles);
	};

	const onRemoveUploadedFile = (id: number, formikBag) => {
		const nextFiles = formikBag.values.files.filter(f => f.id !== id);
		formikBag.setFieldValue('files', nextFiles);
	};

	const onKeyDown = (e: KeyboardEvent) => {
		const formikBag = form.current;

		if (formikBag && e.ctrlKey && e.code === 'Enter') {
			e.preventDefault();
			formikBag.submitForm();
		}
	};

	const handlePaste = (e: React.ClipboardEvent<HTMLTextAreaElement>, formikBag: FormikProps<NewMessage>) => {
		const items = Array.from(e.clipboardData.items).filter(x => /^image\//.test(x.type));

		items.forEach((item) => {
			const blob = item?.getAsFile();

			uploadFile({
				file: blob,
				type: 'chatMessage',
				objectId: -1,
				fileType: FileType.File
			})
				.then(data => onUploadFile(data, formikBag))
				.catch((err: any) => {
					message.error(typeof err === 'object' ? err.message : err);
				});
		});
	};

	React.useEffect(() => {
		document.addEventListener('keydown', onKeyDown);
		return () => document.removeEventListener('keydown', onKeyDown);
	}, []);

	const withTyping = otherComponents.includes(ChatComponents.Mentions);

	const MentionComponent = React.useMemo(() => {
		return withTyping ? ChatFormComponentsDictionary[ChatComponents.Mentions] : Mentions;
	}, [withTyping]);

	const validationSchema = React.useMemo(() => {
		return getValidationSchema(maxMessageLength);
	}, []);

	return <Formik
		initialValues={{
			text: editMessage?.text || '',
			id: editMessage?.id || -1,
			chatId:  editMessage?.chatId || chat.id,
			files: editMessage?.files.map(f => f.file) || [],
			attachments: [],
			bytes: [],
			chatMessageType: ChatMessageType.Regular
		} as NewMessage}
		onSubmit={handleSubmitForm}
		validationSchema={validationSchema}
		enableReinitialize={true}
		render={(formikBag: FormikProps<NewMessage>) => {
			return <Form id="new-chat-message" className="chat-form-component">
				<FormikRef formikRef={form} formikBug={formikBag}/>
				<FormikField
					fieldName="text"
					containerClassName={`chat-form-component__control-container ${withTyping && 'chat-form-component__control-container_typing'}`}
					render={({field, form}: FieldProps<string, NewMessage>) =>
						<MentionComponent
							style={{ width: '100%' }}
							onChange={text => form.setFieldValue(field.name, text)}
							className="form-control"
							placeholder="Start typing your message"
							placement="top"
							value={field.value}
							onPaste={formSettings.allowPasteImages ? e => handlePaste(e, formikBag) : undefined}
							chat={chat}
						>
							{chat.contacts.map((item) => {
								const name = getUserName(item);

								return 	<Mentions.Option
									key={`${item.id}`}
									value={name}
								>
									{name}
								</Mentions.Option>;
							})}
						</MentionComponent>
					}
				/>
				<div className="chat-uploaded-files">
					<div className="chat-uploaded-files" style={{ flexWrap: 'wrap' }}>
						{formikBag.values.files
							.map(f => (
								<Tag
									className="chat-file-tag"
									key={f.id}
									onClose={() => onRemoveUploadedFile(f.id, formikBag)}
									closable
									closeIcon={<i className="fa fa-times"/>}
								>
									{imageExtensions.includes(f.extension?.toLowerCase()) ?
										<img
											onClick={e => onImageClick && onImageClick(e, f)}
											className="chat-form-file-tag-image"
											src={f.thumb} alt={f.name}/> :
										<a href={f.src} target="_blank" download><VerticalAlignBottomOutlined/></a>
									}
									&nbsp;&nbsp;{f.name}&nbsp;&nbsp;
								</Tag>
							))
						}
						<React.Fragment key="additionalFiles">
							{formTags.includes(ChatFormTag.Voice)
								&& ChatFormTagDictionary[ChatFormTag.Voice](formikBag, {state, setState: _setState, waveColor})}
							{formTags.includes(ChatFormTag.Video)
								&& ChatFormTagDictionary[ChatFormTag.Video](formikBag, {state, setState: _setState, waveColor})}
						</React.Fragment>
					</div>
					<div className="chat-message-flex">
						{formSettings.underFormLabel && <span className="chat-message-item__label chat-message-item">
							{formSettings.underFormLabel}
						</span>}
						<React.Fragment key="buttons">
							{formButtons
								.map((button) => {
									const props = {onUploadFile: f => onUploadFile(f, formikBag), setState: _setState, waveColor, isEditMessage: !!editMessage};
									const wrapper = typeof button !== 'function' && chatFormButtonsWrappers[button] ?
										chatFormButtonsWrappers[button] : children => children;

									return typeof button === 'function' ? button(formikBag, props) :
										wrapper ? wrapper(ChatFormButtonsDictionary[button](formikBag, props)) : ChatFormButtonsDictionary[button](formikBag, props);
								})
							}
						</React.Fragment>
						{
							editMessage ? <Button
								className="btn btn-sm chat-message-item btn-danger"
								type="button"
								onClick={() => setEdit(null)}
							>
								Cancel
							</Button> : null
						}
						<Button
							isLoading={state.isLoading}
							className={`btn btn-sm chat-message-item ${sendClassName}`}
							disabled={!formikBag.values.files.length &&
								!formikBag.values.text.length &&
								formikBag.values.bytes.length <= 0}
						>
							Send
						</Button>
					</div>
				</div>
			</Form>;
		}}
	/>;
};

export default ChatMessageForm;
